import _ from 'lodash';
import $ from 'jquery';
import Fuse from 'fuse.js';
import Icon from './Icon';
import Spinner from './Spinner';
import Checkbox from './Checkbox';
import React, { Component } from 'react';
import PropTypes from 'prop-types';

// Import the CSS
import '../../scss/components/_dropdown.scss';

const ESC_KEY_CODE = 27;
const SPACE_KEY_CODE = 32;
const ENTER_KEY_CODE = 13;
const DOWN_ARROW_KEY_CODE = 40;
const UP_ARROW_KEY_CODE = 38;

export default class Dropdown extends Component {

	static propTypes = {
		disabled: PropTypes.bool,
		name: PropTypes.string.isRequired,
		options: PropTypes.array,
		multiple: PropTypes.bool,
		onChange: PropTypes.func,
		multipleLabel: PropTypes.node,
		emptyLabel: PropTypes.node,
		onHideMenu: PropTypes.func,
		onShowMenu: PropTypes.func,
		selected: PropTypes.oneOfType([
			PropTypes.number,
			PropTypes.string,
			PropTypes.object,
			PropTypes.arrayOf(PropTypes.oneOfType([
				PropTypes.number,
				PropTypes.string,
				PropTypes.object,
			])),
		]),
		wrap: PropTypes.bool,
		autoComplete: PropTypes.oneOfType([ PropTypes.bool, PropTypes.func ]),
		placeholder: PropTypes.string,
		searchKeys: PropTypes.array,
		isFetchingOptions: PropTypes.bool,
		autoCompleteDelay: PropTypes.number,
		error: PropTypes.oneOfType([ PropTypes.bool, PropTypes.object ]),
	};

	static defaultProps = {
		options: [],
		searchKeys: [ 'label' ],
		placeholder: 'Search...',
		isFetchingOptions: false,
		autoCompleteDelay: 200,
	};

	state = {
		id: Math.random(),
		highlightedOptionIndex: null,
		showMenu: false,
		inputValue: '',
		options: this.props.options,
		isInputFocused: false,
	};

	constructor (props) {
		super(props);

		this.fuse = new Fuse(props.options, {
			distance: 30,
			threshold: 0.3,
			keys: props.searchKeys,
		});

		this.hideMenu = this.hideMenu.bind(this);
		this.showMenu = this.showMenu.bind(this);
		this.toggleMenu = this.toggleMenu.bind(this);
		this.renderOptions = this.renderOptions.bind(this);
		this.handleAutoComplete = this.handleAutoComplete.bind(this);
	}

	componentWillReceiveProps (props) {
		this.setState({ options: props.options });
	}

	handleAutoComplete (value) {

		// Normalize the value
		const normalizedValue = _.trim(value);

		if (_.isFunction(this.props.autoComplete)) {

			this.setState({ inputValue: value });

			if (this.timer) {
				clearTimeout(this.timer);
			}

			this.timer = setTimeout(() => this.props.autoComplete(normalizedValue), this.props.autoCompleteDelay);

		} else if (this.props.autoComplete === true) {

			this.setState({
				inputValue: value,
				options: value ? this.fuse.search(normalizedValue) : this.props.options,
			});

		}
	}

	handleClick (e) {
		if (!e.isDefaultPrevented() && !this.props.disabled) {

			// Prevent parent click handlers from executing
			e.preventDefault();

			this.toggleMenu();
		}
	}

	handleClickOption (option) {
		this.toggleSelectOption(option);
	}

	toggleSelectOption (option) {

		let isValueSelected = false;

		if (this.props.multiple) {
			if (Array.isArray(this.props.selected)) {

				isValueSelected = this.props.selected.some(selectedValue => {
					if (this.props.autoComplete) {
						return _.isEqual(selectedValue.value, option.value);
					} else {
						return _.isEqual(selectedValue, option.value);
					}
				});

			}
		} else {

			this.hideMenu();

			if (this.props.autoComplete) {
				isValueSelected = !!(this.props.selected && _.isEqual(this.props.selected.value, option.value));
			} else {
				isValueSelected = _.isEqual(this.props.selected, option.value);
			}
		}

		if (isValueSelected === false) {
			this.selectOption(option);
		} else if (this.props.multiple) {
			this.deSelectOption(option);
		}
	}

	selectOption (option) {
		if (this.props.onChange) {

			// Get the currently selected value(s)
			let selected = _.clone(this.props.selected);

			// Initialize the selected value if none exists
			if (!selected) {
				selected = this.props.multiple ? [] : null;
			}

			// Set the new selected value(s)
			if (this.props.multiple) {
				selected.push(this.props.autoComplete ? option : option.value);
			} else {
				selected = this.props.autoComplete ? option : option.value;
			}

			// Execute the onChange handler, sending the new selected value(s)
			this.props.onChange(selected);
		}
	}

	deSelectOption (option) {
		if (this.props.onChange) {

			// Get the currently selected value(s)
			let selected = _.clone(this.props.selected);

			// Stop if there are no selected values, since nothing has changed
			if (!selected) {
				return;
			}

			let newSelected = selected.filter(selectedValue => {
				if (this.props.autoComplete) {
					return !_.isEqual(selectedValue.value, option.props.value);
				} else {
					return !_.isEqual(selectedValue, option.props.value);
				}
			});

			// Execute the onChange handler only if the length has changed
			if (newSelected.length < selected.length) {
				this.props.onChange(newSelected);
			}

		}
	}

	toggleMenu () {
		if (this.state.showMenu) {
			this.hideMenu();
		} else {
			this.showMenu();
		}
	}

	showMenu () {

		let keyDownSelector = `keydown.dropdown-${this.state.id}`;
		let mouseDownSelector = `mousedown.dropdown-${this.state.id}`;

		// Show the menu
		this.setState({ showMenu: true });

		// Listen for key down events so we can traverse the options
		// using the keyboard.
		$(document).off(keyDownSelector).on(keyDownSelector, e => {
			switch (e.which) {

				// Highlight next option when pressing down arrow
				case DOWN_ARROW_KEY_CODE: {

					let highlightedOptionIndex = null;
					let lastOptionIndex = this.props.options.length - 1;

					if (this.state.highlightedOptionIndex === null) {

						// Select the first option
						highlightedOptionIndex = 0;

					} else if (this.state.highlightedOptionIndex < lastOptionIndex) {

						// Increment the index to highlight the next option
						highlightedOptionIndex = this.state.highlightedOptionIndex + 1;

					}

					// Set the new highlighted option
					this.setState({ highlightedOptionIndex });

					e.preventDefault();
					break;
				}

				// Highlight next option when pressing up arrow
				case UP_ARROW_KEY_CODE: {

					let highlightedOptionIndex = null;
					let lastOptionIndex = this.props.options.length - 1;

					if (this.state.highlightedOptionIndex === null) {

						// Select the last option
						highlightedOptionIndex = lastOptionIndex;

					} else if (this.state.highlightedOptionIndex > 0) {

						// Decrement the index to highlight the previous option
						highlightedOptionIndex = this.state.highlightedOptionIndex - 1;

					}

					// Set the new highlighted option
					this.setState({ highlightedOptionIndex });

					e.preventDefault();
					break;
				}

				// Close menu when pressing escape key
				case ESC_KEY_CODE: {
					this.hideMenu();
					e.preventDefault();
					break;
				}

				// [De]Select option when pressing enter or space key
				case ENTER_KEY_CODE:
				case SPACE_KEY_CODE: {
					if (this.state.highlightedOptionIndex in this.props.options) {
						this.toggleSelectOption({
							label: this.props.options[this.state.highlightedOptionIndex].label,
							value: this.props.options[this.state.highlightedOptionIndex].value,
						});
					}
					if (e.which === ENTER_KEY_CODE || !this.state.isInputFocused) {
						e.preventDefault();
					}
					break;
				}
			}
		});

		// Listen for click events outside of the dropdown so
		// we can close the menu when the user clicks outside.
		$(document).off(mouseDownSelector).on(mouseDownSelector, e => {

			// Get the closest dropdown ancestor
			let closestDropdown = $(e.target).closest('.dropdown').get(0);

			// Check if the closest dropdown doesn't match this component.
			// If it doesn't, then hide the menu, since we've clicked
			// outside of the component.
			if (closestDropdown !== this.dropdown) {
				this.hideMenu();
			}
		});

		// Select text in the search field, with a timeout so it gets
		// executed after the next re-render of the component
		if (this.input) {
			setTimeout(() => {
				this.input.focus();
				this.input.select();
			}, 0);
		}

		// Call the onShowMenu() callback, if it exists
		if (this.props.onShowMenu) {
			this.props.onShowMenu();
		}

	}

	hideMenu () {

		// Hide the menu
		this.setState({ showMenu: false });

		// Remove listeners
		$(document).off(`mousedown.dropdown-${this.state.id}`);
		$(document).off(`keydown.dropdown-${this.state.id}`);

		// Call the onHideMenu() callback, if it exists
		if (this.props.onHideMenu) {
			this.props.onHideMenu();
		}

	}

	renderOptions () {
		let options = [];

		if (this.props.error) {
			// Display error message
			options = [
				<Option key="error" value="error" style="error" onClick={ () => {} }>
					<Icon type="warning-circle" className="error"/>
					<span>{ this.props.error.message || 'An error occurred' }</span>
				</Option>
			];
		} else if (this.props.autoComplete && !this.state.options.length && this.state.inputValue && !this.props.isFetchingOptions) {
			// Show 'no search results found' for no auto-complete results
			options = [
				<Option
					key="no-results"
					value="no-results"
					style="no-results"
					onClick={ () => {} }
					label="No search results found."/>
			];
		} else {
			// Construct array of <Option> or <MultiOption> elements
			_.each(this.state.options, (option, index) => {

				let value = option.value || option.label;

				// Set whether this option is highlighted
				option.isHighlighted = this.state.highlightedOptionIndex === index;

				if (this.props.multiple) {

					// Set whether this option is selected or not
					option.isSelected = Array.isArray(this.props.selected) &&
						this.props.selected.some(selectedValue => _.isEqual(selectedValue, value));

					options.push(
						<MultiOption
							key={ 'option-' + index }
							name={ this.props.name }
							label={ option.label }
							value={ value }
							onClick={ option => this.handleClickOption(option) }
							{ ...option } />
					);

				} else {

					// Set whether this option is selected or not
					// Use the `value` field if `selected` is an options object
					let selected = this.props.selected && this.props.selected.hasOwnProperty('value')
						? this.props.selected.value : this.props.selected;

					option.isSelected = _.isEqual(selected, value);

					options.push(
						<Option
							key={ 'option-' + index }
							name={ this.props.name }
							label={ option.label }
							value={ value }
							onClick={ option => this.handleClickOption(option) }
							{ ...option } />
					);

				}
			});
		}

		return options;
	}

	render () {

		// Add additional classes based on the props
		let classes = [ 'dropdown' ];

		if (this.props.style) {
			classes.push(`dropdown-${this.props.style}`);
		}
		if (this.props.className) {
			classes.push(this.props.className);
		}
		if (this.state.showMenu) {
			classes.push('dropdown-open');
		}
		if (this.props.wrap) {
			classes.push('dropdown-wrap');
		}

		// Render options and get the selected options
		let options = this.renderOptions();
		let selectedOptions;

		if (this.props.autoComplete) {
			selectedOptions = this.props.selected;
		} else if (this.props.multiple) {
			selectedOptions = _.filter(this.props.options, option => option.isSelected);
		} else {
			selectedOptions = _.find(this.props.options, option => option.isSelected);
		}

		// If there are no selected options then set the empty label
		let label;

		if (this.props.multiple) {
			if (!selectedOptions || selectedOptions.length === 0) {
				label = this.props.emptyLabel;
			} else if (selectedOptions.length === 1) {
				label = selectedOptions[0].label;
			} else if (this.props.multipleLabel) {
				label = this.props.multipleLabel.replace('{num}', selectedOptions.length);
			} else {
				label = `${selectedOptions.length} Options Selected`;
			}
		} else if (!selectedOptions) {
			label = this.props.emptyLabel;
		} else {
			label = selectedOptions.label;
		}

		let selectProps = _.omit(this.props, [ 'className', 'style' ]);

		let labelContainerClass = 'label-container';
		if (!selectedOptions || selectedOptions.length === 0) {
			labelContainerClass += ' empty';
		}

		return (
			<div ref={ dropdown => this.dropdown = dropdown } className={ classes.join(' ') } onClick={ e => this.handleClick(e) }>
				<div className={labelContainerClass}>
					<label htmlFor={ this.props.name }>
						{ label || '\u00a0' }
					</label>
				</div>
				<div className="dropdown-list">
					<If condition={ this.props.autoComplete }>
						<div className="auto-complete" onClick={ e => e.preventDefault() }>
							<input
								ref={ input => this.input = input }
								value={ this.state.inputValue }
								placeholder={ this.props.placeholder }
								onBlur={ () => this.setState({ isInputFocused: false }) }
								onFocus={ () => this.setState({ isInputFocused: true }) }
								onChange={ e => this.handleAutoComplete(e.target.value) } />
							<Choose>
								<When condition={ this.props.isFetchingOptions }>
									<span className="spinner-icon"><Spinner style="auto-complete"/></span>
								</When>
								<When condition={ !this.props.isFetchingOptions }>
									<span ref="icon" className="searchbar-icon"></span>
								</When>
							</Choose>
						</div>
					</If>
					<div className="dropdown-options">{ options }</div>
				</div>
			</div>
		);
	}

}

class Option extends Component {

	static propTypes = {
		label: PropTypes.node,
		value: PropTypes.oneOfType([ PropTypes.number, PropTypes.string, PropTypes.object ]),
		isSelected: PropTypes.bool,
		isHighlighted: PropTypes.bool,
		onClick: PropTypes.func.isRequired,
		style: PropTypes.string,
	};

	getValue () {
		return this.props.value || this.props.label;
	}

	getContent () {
		if (!this.props.children && !this.props.label) {
			console.warn('An option has been rendered without any content (label and children are missing).');
		}

		return this.props.children || this.props.label;
	}

	getClasses () {

		let classes = [ 'dropdown-option' ];

		if (this.props.style) {
			classes.push(`dropdown-option-${this.props.style}`);
		}
		if (this.props.isSelected) {
			classes.push('dropdown-option-selected');
		}
		if (this.props.isHighlighted) {
			classes.push('dropdown-option-highlighted');
		}

		return classes;
	}

	getProps () {
		return _.omit(this.props, [ 'className', 'isSelected', 'isHighlighted', 'onClick', 'style' ]);
	}

	handleClick () {
		this.props.onClick({
			label: this.props.label,
			value: this.props.value,
		});
	}

	render () {
		return (
			<div className={ this.getClasses().join(' ') } onClick={ e => this.handleClick() } { ...this.getProps() }>
				{ this.getContent() }
			</div>
		);
	}

}

class MultiOption extends Option {

	handleClick (e) {

		// Prevent the menu from closing after clicking an option
		e.preventDefault();

		// Call the default `handleClick()` method
		super.handleClick();

	}

	render () {
		return (
			<div className={ this.getClasses().join(' ') } onClick={ e => this.handleClick(e) } { ...this.getProps() }>
				<Checkbox
					name={ `${this.props.name}[]` }
					checked={ this.props.isSelected }
					onChange={ e => this.handleClick(e) } />
				{ this.getContent() }
			</div>
		);
	}

}



// WEBPACK FOOTER //
// ./src/assets/js/components/Dropdown.js